// Dashboard chart card: yearly reservations vs contact-cards line diagram,
// with a data-type selector and year navigation.
import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Box, Divider, Flex, Select, Spinner, Text, useColorModeValue, useToken } from "@chakra-ui/react";
import { MdShowChart } from "react-icons/md";
import Card from "horizon/components/card/Card";
import CardHeader from "../shared/CardHeader";
import ChartLegend from "../shared/ChartLegend";
import YearNavFooter from "../shared/YearNavFooter";
import SvgLineChart, { MONTH_LABELS } from "../shared/SvgLineChart";

/** Data-type configurations: API url and response keys/labels. */
const TYPES = {
  contacts: {
    url: "/admin/contact_cards/years_stat/", acc: "contact_card_accepted", dec: "contact_card_declined",
    accLabel: "Acceptés", decLabel: "Refusés"
  },
  reservations: {
    url: "/admin/reservation/years_stat/", acc: "reservation_finish", dec: "reservation_declined",
    accLabel: "Terminés", decLabel: "Abandonnés"
  }
};

/**
 * Displays a yearly line diagram for reservations or contact cards. Supports
 * switching the data type and navigating between years.
 * @returns {React.ReactElement}
 */
export default function ReservationDiagramCard() {
  const [type, setType] = useState("contacts");
  const [year, setYear] = useState(new Date().getFullYear());
  const [loading, setLoading] = useState(false);
  const [raw, setRaw] = useState({ acc: [], dec: [] });
  const [green, red] = useToken("colors", ["green.500", "red.500"]);

  const load = useCallback(async (dataType, targetYear) => {
    const cfg = TYPES[dataType];
    setLoading(true);
    try {
      const { data } = await axios.get(cfg.url + targetYear);
      setRaw({ acc: data[cfg.acc], dec: data[cfg.dec] });
    } catch (error) {
      console.error("Error fetching diagram stats:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  const changeYear = useCallback((delta) => {
    const next = year + delta;
    setYear(next);
    load(type, next);
  }, [year, type, load]);

  const onTypeChange = useCallback((e) => {
    setType(e.target.value);
    load(e.target.value, year);
  }, [year, load]);

  useEffect(() => {
    load("contacts", year);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const cfg = TYPES[type];
  const subColor = useColorModeValue("secondaryGray.600", "secondaryGray.400");
  const datasets = [
    { label: cfg.accLabel, values: raw.acc || [], color: green },
    { label: cfg.decLabel, values: raw.dec || [], color: red }
  ];
  const selector = (
    <Flex align="center" gap="8px">
      <Text fontSize="xs" color={subColor}>{year}</Text>
      <Select size="sm" value={type} onChange={onTypeChange} w="160px" borderRadius="10px">
        <option value="contacts">Cartes de contact</option>
        <option value="reservations">Réservations</option>
      </Select>
    </Flex>
  );
  return (
    <Card h="100%">
      <CardHeader icon={MdShowChart} title="Diagramme" accent="blue.500" right={selector} />
      <Divider mb="8px" />
      <Box position="relative">
        {loading && (
          <Flex position="absolute" inset="0" align="center" justify="center" zIndex="2">
            <Spinner color="blue.500" />
          </Flex>
        )}
        <SvgLineChart datasets={datasets} labels={MONTH_LABELS} height="300px" />
      </Box>
      <ChartLegend items={[
        { color: green, label: cfg.accLabel },
        { color: red, label: cfg.decLabel }
      ]} />
      <YearNavFooter loading={loading} onPrev={() => changeYear(-1)} onNext={() => changeYear(1)}
        onRefresh={() => load(type, year)} />
    </Card>
  );
}
